import type * as React from "react";
import { Icon, type IconName } from "../icon/Icon";

/**
 * Inline banner conveying a status message. Tone drives icon and color.
 * Ported from the Wanted Design System bundle (components/feedback/Alert.jsx).
 * Tellvue addition: `role` defaults to "status" for info/positive and "alert" otherwise, so
 * static explanations are not shouted by screen readers.
 */
export interface AlertProps {
  /** @default "info" */
  tone?: "info" | "positive" | "cautionary" | "negative";
  title?: string;
  /** Body text. */
  children?: React.ReactNode;
  /** Optional action node (e.g. a TextButton). */
  action?: React.ReactNode;
  /** Show a close button when provided. */
  onClose?: () => void;
  role?: "alert" | "status" | "note";
  style?: React.CSSProperties;
}

const TONES: Record<NonNullable<AlertProps["tone"]>, { icon: IconName; fg: string; bg: string }> = {
  info: {
    icon: "circleInfoFill",
    fg: "var(--primary-normal)",
    bg: "color-mix(in srgb, var(--primary-normal) 10%, transparent)",
  },
  positive: {
    icon: "circleCheckFill",
    fg: "var(--status-positive)",
    bg: "color-mix(in srgb, var(--status-positive) 8%, transparent)",
  },
  cautionary: {
    icon: "triangleExclamationFill",
    fg: "var(--status-cautionary)",
    bg: "color-mix(in srgb, var(--status-cautionary) 10%, transparent)",
  },
  negative: {
    icon: "circleCloseFill",
    fg: "var(--status-negative)",
    bg: "color-mix(in srgb, var(--status-negative) 8%, transparent)",
  },
};

export function Alert({
  tone = "info",
  title,
  children,
  action,
  onClose,
  role,
  style,
}: AlertProps) {
  const t = TONES[tone] ?? TONES.info;
  const r = role ?? (tone === "info" || tone === "positive" ? "status" : "alert");
  return (
    <div
      role={r}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: 16,
        borderRadius: "var(--radius-md)",
        background: t.bg,
        fontFamily: "var(--font-sans)",
        ...style,
      }}
    >
      <Icon name={t.icon} size={20} style={{ color: t.fg, flex: "none", marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div
            style={{
              fontSize: "var(--font-size-label-1)",
              lineHeight: "var(--line-height-label-1)",
              fontWeight: 600,
              color: "var(--label-normal)",
              marginBottom: children ? 2 : 0,
            }}
          >
            {title}
          </div>
        )}
        {children && (
          <div
            style={{
              fontSize: "var(--font-size-label-1)",
              lineHeight: "var(--line-height-label-1-reading)",
              color: "var(--label-neutral)",
            }}
          >
            {children}
          </div>
        )}
        {action && <div style={{ marginTop: 10 }}>{action}</div>}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            color: "var(--label-alternative)",
            flex: "none",
          }}
        >
          <Icon name="close" size={18} />
        </button>
      )}
    </div>
  );
}

export default Alert;
